import { SvgIcon } from '../../components/icons';
import { useDsTheme } from '../ThemeProvider';
import { IconButton, SearchField, Tooltip } from '../primitives';

type DesignHeaderProps = {
  onToggleSidebar: () => void;
  onOpenSettings: () => void;
  /** Shown as the current location, since the prototype has no router. */
  context?: string;
};

/**
 * Redesigned header.
 *
 * The production header is 40px of logo + profile picture and nothing else —
 * no search, no settings, no notifications, and its colours (`bg-slate-700`,
 * `text-amber-400`) are raw Tailwind palette values rather than tokens.
 *
 * This is 48px: slightly taller, but it now carries global search, the theme
 * gear, a notification affordance and light/dark — so it earns the 8px.
 */
export const DesignHeader = ({
  onToggleSidebar,
  onOpenSettings,
  context,
}: DesignHeaderProps) => {
  const { mode, toggleMode } = useDsTheme();

  return (
    <header className="ds-header">
      <IconButton label="Toggle navigation" onClick={onToggleSidebar}>
        <SvgIcon name="menu" size={18} />
      </IconButton>

      <div className="ds-brand">
        <span className="ds-brand-mark" aria-hidden="true">
          MT
        </span>
        <span>Maintener</span>
      </div>

      {context && (
        <>
          <span className="ds-muted" aria-hidden="true">
            /
          </span>
          <span className="ds-subtitle">{context}</span>
        </>
      )}

      {/* Global search. Hidden below tablet portrait, where the space is
          better spent on the page itself. */}
      <div className="ml-auto hidden md:block" style={{ width: 240 }}>
        <SearchField
          placeholder="Search tails, claims, people…"
          aria-label="Global search"
        />
      </div>

      <div className="ml-auto flex items-center gap-1 md:ml-0">
        <Tooltip label="Notifications">
          <span className="relative inline-flex">
            <IconButton label="Notifications">
              <SvgIcon name="notifications" size={17} />
            </IconButton>
            <span
              className="absolute"
              style={{ top: 3, right: 3, pointerEvents: 'none' }}
            >
              <span className="ds-badge">3</span>
            </span>
          </span>
        </Tooltip>

        <Tooltip label={mode === 'dark' ? 'Switch to light' : 'Switch to dark'}>
          <IconButton
            label={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            onClick={toggleMode}
          >
            <SvgIcon name={mode === 'dark' ? 'lightMode' : 'nightlightRound'} size={17} />
          </IconButton>
        </Tooltip>

        <Tooltip label="Display settings">
          <IconButton label="Open display settings" onClick={onOpenSettings}>
            <SvgIcon name="settings" size={17} />
          </IconButton>
        </Tooltip>
      </div>
    </header>
  );
};
